// src/components/templates/ProfileTemplate.jsx - Complete revision with settings support
import React, { useState, useEffect, useRef } from 'react';
import { 
  User, Building, Briefcase, Package, Heart, Share2, Eye, ChevronDown, ChevronLeft, ChevronRight,
  Mail, Phone, Globe, MapPin, Calendar, Award, ExternalLink, Grid, List, Zap, BarChart3, Shield,
  MessageCircle, Settings, Edit, Play, ZoomIn, X, CheckCircle, Users, LinkIcon, File, Plus,
  Trophy, Star, Clock, Download, FileText, Target, DollarSign, 
  Image as ImageIcon, BookmarkPlus, Github, Linkedin, Twitter, Facebook, Instagram, Youtube
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { MathRenderer } from '../math/MathRenderer';
import { profileService } from '../../services/profileService';
import { firestoreService } from '../../services/firestoreService';
import { ShareModal } from '../books/modals/ShareModal';
import { useAuth } from '../../contexts/AuthContext';
import DemoSection from '../profile/sections/DemoSection';
import ProfileContactSection from '../profile/sections/ProfileContactSection';
import OpenChatWidget from '../chat/OpenchatWidget';
import { TestimonialSubmissionModal } from '../../components/profile/forms/TestimonialsModal';
import { TestimonialViewModal } from '../../components/profile/testimonials/TestimonialViewModal';
import { ProfileManager } from '../profile/ProfileManager';
import { SharedHeader } from '../shared/SharedHeader';

const SECTION_ICON_MAP = {
  about: User,
  hero: User,
  contact: Mail,
  experience: Briefcase,
  education: Calendar,
  skills: Settings,
  projects: Grid,
  services: Package,
  achievements: Trophy,
  features: Zap,
  gallery: ImageIcon,
  testimonials: MessageCircle,
  references: Users,
  story: FileText,
  demo: Play,
  specifications: FileText,
  credentials: Award,
  pricing: DollarSign,
  engagement: Heart,
  stats: BarChart3
};

const SECTION_LABEL_MAP = {
  about: 'About',
  hero: 'Hero',
  contact: 'Connect',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Portfolio',
  services: 'Services',
  achievements: 'Awards',
  features: 'Features',
  gallery: 'Gallery',
  testimonials: 'Reviews',
  references: 'References',
  story: 'Story',
  demo: 'Demo',
  specifications: 'Specs',
  credentials: 'Credentials',
  pricing: 'Pricing',
  engagement: 'Engagement',
  stats: 'Stats'
};


export const ProfileTemplate = ({ 
  bookId, 
  book, 
  messages = [], 
  bookData,
  onInteraction,
  sections = [], 
  styling = {},
  profileSettings = {},
  customCSS = '',
  viewManager
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profileSections, setProfileSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroHeight, setHeroHeight] = useState(300);
  const { viewMode, isAuthenticated, hasManagementAccess } = viewManager || { viewMode: 'public', isAuthenticated: false, hasManagementAccess: false };
  const [isOwner, setIsOwner] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [showShareModal, setShowShareModal] = useState(false);

  const [isChatOpen, setIsChatOpen] = useState(false);

  const [interactions, setInteractions] = useState({
    liked: false,
    viewCount: 0,
    likeCount: 0
  });

  const heroRef = useRef(null);
  const contentRef = useRef(null);

  // Get profile type and settings
  const profileType = book?.subCategory || 'personal';
  const layout = profileSettings?.layout || 'modern';
  const theme = profileSettings?.theme || 'professional';
  const sectionLayout = profileSettings?.sectionLayout || 'card';
  const heroStyle = profileSettings?.heroStyle || 'banner';
  const sectionVisibility = profileSettings?.sectionVisibility || {};
  const sectionOrder = profileSettings?.sectionOrder || ['hero', 'about', 'experience', 'skills', 'projects', 'achievements', 'contact'];
  
  const getAvailableTabs = () => {
    // Get visible sections based on sectionVisibility and sectionOrder
    const visibleSectionTypes = sectionOrder.filter(sectionType => {
      // Skip hero section in tabs (it's not a tab)
      if (sectionType === 'hero') return false;
      
      // Check if section is visible (default to true if not specified)
      const isVisible = sectionVisibility[sectionType] !== false;
      
      // Check if section actually exists in profileSections
      const hasSection = profileSections.some(section => section.type === sectionType);
      
      return isVisible && hasSection;
    });

    // Generate tabs from visible sections
    const tabs = visibleSectionTypes.map(sectionType => {
      const icon = SECTION_ICON_MAP[sectionType] || User;
      const label = SECTION_LABEL_MAP[sectionType] || sectionType.charAt(0).toUpperCase() + sectionType.slice(1);
      
      return {
        id: sectionType,
        label,
        icon
      };
    });

    return tabs;
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Scroll to the section - target the top of the section card
    const element = document.getElementById(`section-${tabId}`);
    if (element && contentRef.current) {
      // Get the section wrapper for better positioning
      const sectionWrapper = element.closest('.section-wrapper') || element;
      
      // Calculate offset to account for sticky header and some padding
      const headerHeight = 0; // Adjust based on your sticky header height
      const paddingOffset = 0; // Additional padding for better UX
      const offsetTop = sectionWrapper.offsetTop - headerHeight - paddingOffset;
      
      contentRef.current.scrollTo({
        top: Math.max(0, offsetTop), // Ensure we don't scroll to negative values
        behavior: 'smooth'
      });
    }
  };

  // Updated scroll-based active tab detection
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const availableTabs = getAvailableTabs();
      const sections = availableTabs.map(tab => ({
        id: tab.id,
        element: document.getElementById(`section-${tab.id}`)
      })).filter(section => section.element);

      if (sections.length === 0) return;

      // More precise scroll detection
      const scrollTop = contentRef.current.scrollTop;
      const containerHeight = contentRef.current.clientHeight;
      const headerOffset = 120; // Account for sticky header

      // Find the section that's most visible in the viewport
      let activeSection = sections[0].id; // Default to first section
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const element = section.element;
        const rect = element.getBoundingClientRect();
        const containerRect = contentRef.current.getBoundingClientRect();
        
        // Calculate relative position within the scrollable container
        const elementTop = rect.top - containerRect.top;
        const elementBottom = rect.bottom - containerRect.top;
        
        // Check if section is visible and prominent in viewport
        if (elementTop <= headerOffset && elementBottom > headerOffset) {
          activeSection = section.id;
        }
      }
      
      if (activeTab !== activeSection) {
        setActiveTab(activeSection);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      // Also run once to set initial tab
      handleScroll();
      
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [profileSections, activeTab, sectionOrder, sectionVisibility]);


  useEffect(() => {
    if (profileSections.length > 0) {
      const availableTabs = getAvailableTabs();
      if (availableTabs.length > 0 && (!activeTab || !availableTabs.some(tab => tab.id === activeTab))) {
        setActiveTab(availableTabs[0].id);
      }
    }
  }, [profileSections, sectionOrder, sectionVisibility]);
  
  useEffect(() => {
    if (viewMode === 'management') {
        setLoading(false);
        return;
      }
    loadProfileData();
    trackProfileView();
    applyCustomStyling();
  }, [bookId]);

  useEffect(() => {
    applyCustomStyling();
  }, [styling, profileSettings, customCSS]);

  const loadProfileData = async () => {
    try {
      const sections = await profileService.getProfileSections(bookId);
      setProfileSections(sections);
      
      const interactionData = await profileService.getProfileInteractions(bookId, user?.uid);
      setInteractions(interactionData);
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const measureHeroHeight = () => {
      if (heroRef.current && window.innerWidth >= 768) {
        const height = heroRef.current.offsetHeight;
        setHeroHeight(height);
        document.documentElement.style.setProperty('--hero-height', `${height}px`);
      } else {
        // Clear the height on mobile
        document.documentElement.style.setProperty('--hero-height', '0px');
      }
    };

    measureHeroHeight();
    window.addEventListener('resize', measureHeroHeight);
    return () => window.removeEventListener('resize', measureHeroHeight);
  }, [profileSections]);

  const trackProfileView = async () => {
    try {
      await profileService.trackView(bookId, user?.uid);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const applyCustomStyling = () => {
    if (!book) return;
    
    const root = document.documentElement;
    
    // Apply CSS custom properties for dynamic styling
    root.style.setProperty('--profile-primary-color', styling.primaryColor || '#6366f1');
    root.style.setProperty('--profile-background-color', styling.backgroundColor || '#f8fafc');
    root.style.setProperty('--profile-header-color', styling.headerColor || '#ffffff');
    root.style.setProperty('--profile-header-text-color', styling.headerTextColor || '#1f2937');
    root.style.setProperty('--profile-accent-color', styling.accentColor || '#8b5cf6');
    root.style.setProperty('--profile-font-family', styling.fontFamily || 'Helvetica, sans-serif');
    
    // Font size mapping
    const fontSizes = { small: '14px', medium: '16px', large: '18px' };
    root.style.setProperty('--profile-font-size', fontSizes[styling.fontSize] || '16px');
    
    // Border radius mapping
    const borderRadiuses = { none: '0px', small: '4px', medium: '8px', large: '16px' };
    root.style.setProperty('--profile-border-radius', borderRadiuses[styling.borderRadius] || '8px');
    
    // Shadow level mapping
    const shadowLevels = {
      none: 'none',
      subtle: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      strong: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    };

    const styleElement = document.getElementById('profile-fixed-hero-css') || document.createElement('style');
    styleElement.id = 'profile-fixed-hero-css';
   
    if (!document.getElementById('profile-fixed-hero-css')) {
      document.head.appendChild(styleElement);
    };

    root.style.setProperty('--profile-shadow', shadowLevels[styling.shadowLevel] || shadowLevels.subtle);

    // Apply custom CSS
    if (customCSS) {
      const styleId = 'profile-custom-styles';
      let styleElement = document.getElementById(styleId);
      
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      
      styleElement.textContent = customCSS;
    }
  };

  const handleInteraction = (type, data) => {
    if (type === 'contact') {
      // Toggle chat instead of default contact behavior
      setIsChatOpen(true);
    } else {
      // Handle other interactions as before
      onInteraction(type, data);
    }
  };

  const handleContact = async (method, value) => {
    try {
      await profileService.trackContact(bookId, method, user?.uid);
      
      switch (method) {
        case 'email':
          window.location.href = `mailto:${value}`;
          break;
        case 'phone':
          window.location.href = `tel:${value}`;
          break;
        case 'website':
          window.open(value, '_blank');
          break;
        case 'whatsapp':
          window.open(`https://wa.me/${value.replace(/\D/g, '')}`, '_blank');
          break;
      }
    } catch (error) {
      console.error('Error handling contact:', error);
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
      
    // Track share button click
    firestoreService.trackInteraction(bookId, {
      type: 'share_click',
      userId: user?.uid
    });
  };

  if (loading) {
    return <ProfileLoadingSpinner />;
  }

  // Get the container classes based on layout and theme
  const getContainerClasses = () => {
    const base = 'min-h-screen profile-template';
    const layoutClasses = {
      modern: 'modern-layout',
      classic: 'classic-layout', 
      minimal: 'minimal-layout',
      creative: 'creative-layout'
    };
    const themeClasses = {
      professional: 'professional-theme',
      creative: 'creative-theme',
      corporate: 'corporate-theme',
      personal: 'personal-theme'
    };
    
    return `${base} ${layoutClasses[layout]} ${themeClasses[theme]}`;
  };

  const handleContactReference = (reference) => {
    // Function to extract profile owner name from various sources
    const getProfileOwnerName = () => {
      // Check hero section first
      const heroSection = sections.find(section => section.type === 'hero');
      if (heroSection) {
        if (heroSection.name) return heroSection.name;
        if (heroSection.fullName) return heroSection.fullName;
        if (heroSection.firstName && heroSection.lastName) {
          return `${heroSection.firstName} ${heroSection.lastName}`;
        }
        if (heroSection.firstName) return heroSection.firstName;
      }

      // Check about section
      const aboutSection = sections.find(section => section.type === 'about');
      if (aboutSection) {
        if (aboutSection.name) return aboutSection.name;
        if (aboutSection.fullName) return aboutSection.fullName;
      }

      // Check contact section
      const contactSection = sections.find(section => section.type === 'contact');
      if (contactSection) {
        if (contactSection.name) return contactSection.name;
        if (contactSection.fullName) return contactSection.fullName;
      }

      // Fallback to book-level data
      if (bookData?.botName) return bookData.botName;
      if (bookData?.title) return bookData.title;
      if (book?.botName) return book.botName;
      if (book?.title) return book.title;
      
      return 'the profile owner';
    };

    const profileOwnerName = getProfileOwnerName();

    if (reference.email) {
      const subject = encodeURIComponent(`Reference inquiry for ${profileOwnerName}`);
      const body = encodeURIComponent(
        `Hello ${reference.name},\n\n` +
        `I am reaching out to inquire about a reference for ${profileOwnerName}. ` +
        `I understand you worked with them as their ${reference.relationship?.replace('-', ' ') || 'colleague'}` +
        `${reference.company ? ` at ${reference.company}` : ''}.\n\n` +
        `I would appreciate any information you could share about your professional experience working with them.\n\n` +
        `Thank you for your time.\n\n` +
        `Best regards`
      );
      
      window.open(`mailto:${reference.email}?subject=${subject}&body=${body}`);
    } else if (reference.phone) {
      if (window.confirm(`Would you like to call ${reference.name} at ${reference.phone}?`)) {
        window.open(`tel:${reference.phone}`);
      }
    } else if (reference.linkedInUrl) {
      if (window.confirm(`Would you like to visit ${reference.name}'s LinkedIn profile?`)) {
        window.open(reference.linkedInUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      alert(`Contact information not available for ${reference.name}`);
    }
  };

  if (viewMode === 'management' && hasManagementAccess) {
      return (
        <>
          <ProfileManager
            user={user}
            bookId={bookId}
            book={book}
            onUpdate={loadProfileData}
            customStyling={styling}
          />
          
        </>
      );
    }  

  return (
    <div 
      className="profile-template-responsive profile-template"
      style={{
        styling
      }}
    >
      {/* Profile Header */}
      <ProfileHeader 
        profileSections={profileSections}
        profileType={profileType}
        book={book}
        bookId={bookId}
        isOwner={isOwner}
        navigate={navigate}
        interactions={interactions}
        onInteraction={handleInteraction}
        heroStyle={heroStyle}
        layout={layout}
        theme={theme}
        profileSettings={profileSettings}
        styling={styling}

        activeTab={activeTab}
        onTabChange={handleTabChange}
        onShare={handleShare}
        availableTabs={getAvailableTabs()}
      />

      {/* Content - Scrollable on desktop, normal on mobile */}
      <div 
        ref={contentRef}
        className="profile-content"
        style={{ 
          maxHeight: 'calc(100vh - 80px)', 
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        <div className={getContentLayoutClasses()}>
          <ProfileMainContent 
            profileSections={profileSections}
            profileType={profileType}
            sectionLayout={sectionLayout}
            sectionVisibility={sectionVisibility}
            sectionOrder={sectionOrder}
            showSectionIcons={profileSettings?.showSectionIcons}
            showSectionNumbers={profileSettings?.showSectionNumbers}
            enableSectionCollapse={profileSettings?.enableSectionCollapse}
            onContactReference={handleContactReference}
            layout={layout}
            // NEW: Pass content ref for section IDs
            contentRef={contentRef}
          />

          {/* Sidebar for classic/modern layouts */}
          {(layout === 'classic' || layout === 'modern') && (
            <ProfileSidebar
              profileSections={profileSections}
              profileType={profileType}
              interactions={interactions}
              onContact={handleContact}
              profileSettings={profileSettings}
              layout={layout}
            />
          )}

          <ShareModal 
            content={bookData}
            contentType="profile" 
            isOpen={showShareModal} 
            onClose={() => setShowShareModal(false)}
          />
        </div>
      </div>
      
      <OpenChatWidget
        userId={bookData.id || book.pageId}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        config={{
          greeting: `Hi! I'm ${profileSections.find(s => s.type === 'hero')?.name || book.botName || 'here to help'}. How can I help you today?`,
          theme: book.styling?.theme || 'light'
        }}
      />
    </div>
  );

  // Existing helper functions remain the same...
  function getContentLayoutClasses() {
    const layouts = {
      modern: 'max-w-5xl mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-4 gap-8',
      classic: 'max-w-5xl mx-auto px-6 py-4 grid grid-cols-1 lg:grid-cols-3 gap-10',
      minimal: 'max-w-5xl mx-auto px-8 py-4 space-y-12',
      creative: 'max-w-full px-4 py-6'
    };
    return layouts[layout] || layouts.modern;
  }

  function getTemplateClasses() {
    const base = 'profile-template';
    const layoutClasses = {
      modern: 'modern-layout',
      classic: 'classic-layout', 
      minimal: 'minimal-layout',
      creative: 'creative-layout'
    };
    const themeClasses = {
      professional: 'professional-theme',
      creative: 'creative-theme',
      corporate: 'corporate-theme',
      personal: 'personal-theme'
    };
    
    return `${base} ${layoutClasses[layout]} ${themeClasses[theme]}`;
  }
};

// Enhanced Profile Header Component
const ProfileHeader = ({ 
  profileSections, 
  profileType, 
  book, 
  bookId,
  isOwner,
  navigate,
  interactions, 
  onInteraction, 
  heroStyle, 
  layout, 
  theme, 
  profileSettings, 
  activeTab, 
  onTabChange, 
  styling = {}, 
  availableTabs,
  onShare
}) => {
  const heroSection = profileSections.find(s => s.type === 'hero') || {};
  const hasCoverImage = Boolean(heroSection.heroImageUrl);
  const navigationTabs = availableTabs;
  const [showShareModal, setShowShareModal] = useState(false);

  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [publicTestimonialCount, setPublicTestimonialCount] = useState(0);
  const [showTestimonialViewModal, setShowTestimonialViewModal] = useState(false);
  
  // PRESERVED: Exact existing getProfileIcon function
  const getProfileIcon = () => {
    const icons = {
      personal: User,
      organization: Building,
      project: Briefcase,
      asset: Package,
      product: Package,
      service: Briefcase
    };
    return icons[profileType] || User;
  };

  const ProfileIcon = getProfileIcon();

  // ENHANCED: Get customizable styling with overlay settings
  const getCustomStyles = () => {
    // Use styling colors or fallback to defaults
    const primaryColor = book.styling?.primaryColor || '#6366f1';
    const accentColor = book.styling?.accentColor || '#8b5cf6';

    // Check if features are enabled
    const enableOverlay = heroSection.enableOverlay !== false;
    const enableProfileBorder = heroSection.enableProfileBorder !== false;
    const enableProfileBackground = heroSection.enableProfileBackground !== false;

    // Hero section specific customizations from heroSection data
    const overlayOpacity = enableOverlay 
      ? (heroSection.overlayOpacity !== undefined ? heroSection.overlayOpacity : 0.5)
      : 0;
    
    const profileImageBorder = enableProfileBorder 
      ? (heroSection.profileImageBorder || {
          width: 4,
          style: 'solid',
          color: '#ffffff'
        })
      : {
          width: 0,
          style: 'none',
          color: 'transparent'
        };
    
    // Profile image background handling
    let profileImageBackground = 'transparent';
    let profileImageBackdropFilter = 'none';
    
    if (enableProfileBackground) {
      const bgType = heroSection.profileImageBackgroundType || 'solid';
      
      if (bgType === 'gradient' && heroSection.profileImageGradient) {
        profileImageBackground = `linear-gradient(135deg, ${heroSection.profileImageGradient.start || primaryColor}, ${heroSection.profileImageGradient.end || accentColor})`;
      } else if (bgType === 'blur') {
        profileImageBackground = 'rgba(255, 255, 255, 0.1)';
        profileImageBackdropFilter = `blur(${heroSection.profileImageBlur || 8}px)`;
      } else if (heroSection.profileImageBackground) {
        profileImageBackground = heroSection.profileImageBackground;
      } else {
        profileImageBackground = 'rgba(255, 255, 255, 0.1)';
      }
    }

    // Profile image dimensions and position
    const profileImageWidth = heroSection.profileImageWidth || 160;
    const profileImageHeight = heroSection.profileImageHeight || 160;
    const profileImageHorizontal = heroSection.profileImageHorizontal || 'left';
    const profileImageVertical = heroSection.profileImageVertical || 'top';
    const profileImageFit = heroSection.profileImageFit || 'cover';
    
    return {
      primaryColor,
      accentColor,
      overlayOpacity,
      profileImageBorder,
      profileImageBackground,
      profileImageBackdropFilter,
      profileImageWidth,
      profileImageHeight,
      profileImageHorizontal,
      profileImageVertical,
      profileImageFit,
      // Generate gradient from primary to accent color
      gradient: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`,
      // Generate darker gradient for better contrast with text
      gradientDark: `linear-gradient(135deg, ${primaryColor}dd, ${accentColor}dd)`,
      // Boolean flags for conditional rendering
      enableOverlay,
      enableProfileBorder,
      enableProfileBackground
    };
  };

  useEffect(() => {
    const getTestimonialCount = () => {
      const testimonialSection = profileSections.find(s => s.type === 'testimonials');
      if (testimonialSection && testimonialSection.items) {
        const publicCount = testimonialSection.items.filter(item => 
          item.showOnWebsite === true
        ).length;
        setPublicTestimonialCount(publicCount);
      }
    };

    getTestimonialCount();
  }, [profileSections]);

  const customStyles = getCustomStyles();

  // Enhanced navigation tabs based on profile type (preserved)
  const getNavigationTabs = () => {
    const baseTabs = [
      { id: 'about', label: 'About', icon: User }
    ];

    const personalTabs = [
      { id: 'experience', label: 'Experience', icon: Briefcase },
      { id: 'skills', label: 'Skills', icon: Settings },
      { id: 'projects', label: 'Portfolio', icon: Grid },
      { id: 'achievements', label: 'Awards', icon: Trophy }
    ];

    const productTabs = [
      { id: 'story', label: 'Story', icon: FileText },
      { id: 'features', label: 'Features', icon: Zap },
      { id: 'demo', label: 'Demo', icon: Play },
      { id: 'specifications', label: 'Specs', icon: FileText },
      { id: 'pricing', label: 'Pricing', icon: DollarSign },
      { id: 'testimonials', label: 'Reviews', icon: MessageCircle }
    ];

    const serviceTabs = [
      { id: 'story', label: 'Story', icon: FileText },
      { id: 'services', label: 'Services', icon: Package },
      { id: 'demo', label: 'Demo', icon: Play },
      { id: 'pricing', label: 'Packages', icon: DollarSign },
      { id: 'testimonials', label: 'Reviews', icon: MessageCircle }
    ];

    const contactTab = { id: 'contact', label: 'Connect', icon: Mail };

    let tabs = [...baseTabs];

    if (profileType === 'product' || profileType === 'asset') {
      tabs.splice(1, 0, ...productTabs);
    } else if (profileType === 'service') {
      tabs.splice(1, 0, ...serviceTabs);
    } else {
      tabs.splice(1, 0, ...personalTabs);
    }

    tabs.push(contactTab);

    // Filter tabs based on available sections
    return tabs.filter(tab => 
      profileSections.some(section => section.type === tab.id)
    );
  };

  const handleCustomButtonAction = (button) => {
    if (button.action === 'custom' && button.url) {
      if (button.url.startsWith('http')) {
        window.open(button.url, '_blank');
      } else {
        window.location.href = button.url;
      }
    } else {
      const element = document.getElementById(`section-${button.action}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      Calendar, Star, Award, FileText, Play
    };
    return iconMap[iconName] || null;
  };

  const getActionButtons = () => {

      return [
        {
          key: 'testimonial',
          label: 'Share Experience',
          icon: Plus,
          onClick: () => setShowTestimonialModal(true),
          variant: 'primary',
          badge: null
        },
        {
          key: 'view-testimonials',
          label: 'Read Reviews',
          icon: MessageCircle,
          onClick: () => {
            const testimonialsSection = document.getElementById('testimonials-section');
            if (testimonialsSection) {
              testimonialsSection.scrollIntoView({ behavior: 'smooth' });
            }
          },
          variant: 'secondary',
          badge: publicTestimonialCount > 0 ? publicTestimonialCount : null
        },
        {
          key: 'contact',
          label: 'Get in Touch',
          icon: Mail,
          onClick: () => {
            const contactSection = document.getElementById('contact-section');
            if (contactSection) {
              contactSection.scrollIntoView({ behavior: 'smooth' });
            }
          },
          variant: 'secondary'
        },
        {
          key: 'share',
          label: 'Share Profile',
          icon: Share2,
          onClick: onShare,
          variant: 'ghost'
        }
      ];
    };

    const actionButtons = getActionButtons();

  const renderEnhancedHero = () => (
    <>
      {/* ENHANCED: Customizable full-width immersive hero section */}
      <div 
        className="relative h-[90vh] overflow-hidden"
        style={{ background: customStyles.gradient }}
      >
        {/* Customizable background with cover image */}
        <div className="absolute inset-0 h-[90vh]">
          {hasCoverImage && (
            <>
              <img
                src={heroSection.heroImageUrl}
                alt="Hero Background"
                className="w-full h-[90vh] object-cover"
              />
              {customStyles.enableOverlay && (
                <div 
                  className="absolute inset-0"
                  style={{ 
                    background: customStyles.gradientDark,
                    opacity: customStyles.overlayOpacity 
                  }}
                />
              )}
            </>
          )}
          {!hasCoverImage && (
            <div 
              className="absolute inset-0"
              style={{ 
                background: `linear-gradient(45deg, ${customStyles.primaryColor}22, ${customStyles.accentColor}22)`,
                opacity: 0.3 
              }}
            />
          )}
        </div>

         

        {/* Animated particles with custom colors */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-pulse"
              style={{
                background: `${customStyles.accentColor}33`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        
        {/* hero content with custom styling */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 gap-y-8 h-[90vh] lg:h-[90vh] flex flex-col justify-center">
          
          <SharedHeader
            templateType="profile"
            bookData={book}
            styling={styling}
            isOwner={isOwner}
            navigate={navigate}
            // pass className or styles down if SharedHeader accepts them
            className="fixed top-0 left-0 right-0 z-50"
            
          />
              {/* Customizable profile image with configured dimensions and position */}
              <div 
                className="rounded-full overflow-hidden"
                style={{
                  width: `${customStyles.profileImageWidth}px`,
                  height: `${customStyles.profileImageHeight}px`,
                  border: `${customStyles.profileImageBorder.width}px ${customStyles.profileImageBorder.style} ${customStyles.profileImageBorder.color}`,
                  background: customStyles.profileImageBackground,
                  backdropFilter: customStyles.profileImageBackdropFilter,
                  padding: '4px', 
                  // Apply horizontal alignment
                  marginLeft: customStyles.profileImageHorizontal === 'center' ? 'auto' : 
                              customStyles.profileImageHorizontal === 'right' ? 'auto' : '0',
                  marginRight: customStyles.profileImageHorizontal === 'center' ? 'auto' : 
                               customStyles.profileImageHorizontal === 'right' ? '0' : 'auto',
                }}
              >
                {heroSection.profileImageUrl ? (
                  <img 
                    src={heroSection.profileImageUrl}
                    alt={heroSection.name}
                    className="w-full h-full"
                    style={{ objectFit: customStyles.profileImageFit,
                             borderRadius: '50%',
                             padding: '1px',
                             boxShadow: `
                              inset 0 1px 3px rgba(0, 0, 0, 0.3),
                              0 0 8px rgba(255, 255, 255, 0.3)
                            `,
                          }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/10">
                    <ProfileIcon 
                      className="text-white/80"
                      style={{
                        width: `${customStyles.profileImageWidth * 0.5}px`,
                        height: `${customStyles.profileImageHeight * 0.5}px`
                      }}
                    />
                  </div>
                )}
              </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="mt-auto space-y-6 mb-8">
              {/* Left side - Profile content */}
              <div className="text-white">

                {/* Enhanced typography */}
                <div>
                  <h1 className="text-3xl lg:text-5xl font-bold mb-12 leading-tight">
                    {heroSection.name || ''}
                  </h1>
                  
                  {heroSection.tagline && (
                    <p className="text-lg lg:text-xl text-white/80 leading-relaxed max-w-lg">
                      {heroSection.tagline || ''}
                    </p>
                  )}
                </div>

                {/* Enhanced action buttons with custom colors */}
                
                <div className="flex flex-wrap gap-4 pt-4">
                {/* Testimonial buttons - only show for non-owners */}
                
                  {/* Testimonial Button */}
                  {heroSection.standardButtons?.testimonial?.enabled !== false && (
                    <button
                      onClick={() => setShowTestimonialModal(true)}
                      className="relative px-8 py-4 rounded-lg font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 text-lg shadow-2xl"
                      style={{
                        background: 'white',
                        color: customStyles.primaryColor
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'white';
                      }}
                    >
                      <Plus className="w-5 h-5" />
                      <span className="hidden sm:inline">
                        {heroSection.standardButtons?.testimonial?.text || 'Share Experience'}
                      </span>
                    </button>
                  )}

                  {/* Reviews Button */}
                  {heroSection.standardButtons?.reviews?.enabled !== false && (
                    <button
                      onClick={() => setShowTestimonialViewModal(true)}
                      className="relative border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white transition-colors flex items-center gap-2"
                      style={{
                        background: customStyles.accentColor,
                        color: publicTestimonialCount > 0 ? 'white' : 'rgba(255,255,255,0.8)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = customStyles.primaryColor;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = 'white';
                      }}
                    >
                      <MessageCircle className="h-5 w-5" />
                      <span className="hidden sm:inline">
                        {heroSection.standardButtons?.reviews?.text || 'Reviews'}
                      </span>
                      {/* Badge for testimonial count */}
                      {heroSection.standardButtons?.reviews?.showBadge !== false && publicTestimonialCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-semibold">
                          {publicTestimonialCount}
                        </span>
                      )}
                    </button>
                  )}

                  {/* Contact Button */}
                  {heroSection.standardButtons?.contact?.enabled !== false && (
                    <button
                      onClick={() => onInteraction('contact')}
                      className="px-8 py-4 rounded-lg font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 text-lg shadow-3xl"
                      style={{
                        background: customStyles.accentColor,
                        color: customStyles.primaryColor
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'white';
                      }}
                    >
                      <Mail className="w-5 h-5" />
                      <span className="hidden sm:inline">
                        {heroSection.standardButtons?.contact?.text || 'Get In Touch'}
                      </span>
                    </button>
                  )}
                  
                  {/* Share Button */}
                  {heroSection.standardButtons?.share?.enabled !== false && (
                    <button 
                      className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-emerald-600 transition-colors flex items-center gap-2"
                      onClick={onShare}
                    >
                      <Share2 className="h-5 w-5" />
                      <span className="hidden sm:inline">
                        {heroSection.standardButtons?.share?.text || 'Share Profile'}
                      </span>
                    </button>
                  )}

                  {/* Download Button */}
                  {heroSection.standardButtons?.download?.enabled !== false && (
                    <button
                      onClick={() => onInteraction('download')}
                      className="border-2 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 text-lg"
                      style={{
                        borderColor: `${customStyles.primaryColor}88`,
                        background: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = `${customStyles.primaryColor}22`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      <Download className="w-5 h-5" />
                      <span className="hidden sm:inline">
                        {heroSection.standardButtons?.download?.text || (
                          profileType === 'personal' ? 'Download CV' : 
                          profileType === 'product' ? 'Download Specs' :
                          profileType === 'service' ? 'Download Brochure' : 'Download Info'
                        )}
                      </span>
                    </button>
                  )}

                  {/* Custom Buttons */}
                  {heroSection.customButtons && heroSection.customButtons.map((button, index) => {
                    if (!button.text) return null;

                    const IconComponent = button.icon ? getIconComponent(button.icon) : null;
                    
                    const buttonStyles = button.style === 'filled'
                      ? { background: 'white', color: customStyles.primaryColor }
                      : button.style === 'outlined'
                      ? { background: 'transparent', border: '2px solid white', color: 'white' }
                      : { background: 'transparent', color: 'white', border: `2px solid ${customStyles.primaryColor}88` };

                    return (
                      <button
                        key={`custom-${index}`}
                        onClick={() => handleCustomButtonAction(button)}
                        className="px-8 py-4 rounded-lg font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 text-lg shadow-lg"
                        style={buttonStyles}
                        onMouseEnter={(e) => {
                          if (button.style === 'filled') {
                            e.target.style.background = '#f8f9fa';
                          } else {
                            e.target.style.background = `${customStyles.primaryColor}22`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = buttonStyles.background;
                        }}
                      >
                        {IconComponent && <IconComponent className="w-5 h-5" />}
                        <span className="hidden sm:inline">{button.text}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Social links with custom hover colors */}
                {heroSection.showSocialLinks !== false && heroSection.socialLinks && heroSection.socialLinks.length > 0 && (
                  <div className="flex gap-4 pt-6">
                    {heroSection.socialLinks.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 border rounded-full flex items-center justify-center text-white transition-colors backdrop-blur-sm"
                        style={{ borderColor: `${customStyles.accentColor}66` }}
                        onMouseEnter={(e) => {
                          e.target.style.background = `${customStyles.accentColor}33`;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                        }}
                      >
                        {link.platform === 'linkedin' && <Linkedin className="w-5 h-5" />}
                        {link.platform === 'github' && <Github className="w-5 h-5" />}
                        {link.platform === 'twitter' && <Twitter className="w-5 h-5" />}
                        {link.platform === 'facebook' && <Facebook className="w-5 h-5" />}
                        {link.platform === 'instagram' && <Instagram className="w-5 h-5" />}
                        {link.platform === 'youtube' && <Youtube className="w-5 h-5" />}
                        {link.platform === 'website' && <Globe className="w-5 h-5" />}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Right side - Stats and highlights with custom styling */}
            <div className="text-white mt-auto items-center space-y-8 mb-8">
              {/* Quick stats cards with configured stats from HeroFormFields */}
              {heroSection.selectedStats && heroSection.selectedStats.length > 0 && (
                <div className="grid grid-cols-2 gap-6">
                  {heroSection.selectedStats.map((statKey, idx) => {
                    // Get the stat configuration from PROFILE_TYPE_STATS
                    const PROFILE_TYPE_STATS = {
                      personal: [
                        { key: 'experience', label: 'Years Experience', icon: '💼' },
                        { key: 'projects', label: 'Projects Completed', icon: '🚀' },
                        { key: 'skills', label: 'Skills Mastered', icon: '⭐' },
                        { key: 'achievements', label: 'Achievements', icon: '🏆' },
                        { key: 'connections', label: 'Professional Connections', icon: '👥' },
                        { key: 'education', label: 'Education Level', icon: '🎓' }
                      ],
                      organization: [
                        { key: 'founded', label: 'Years in Business', icon: '⏰' },
                        { key: 'employees', label: 'Team Members', icon: '👥' },
                        { key: 'clients', label: 'Happy Clients', icon: '💬' },
                        { key: 'projects', label: 'Projects Delivered', icon: '✅' },
                        { key: 'awards', label: 'Awards Won', icon: '🏆' },
                        { key: 'locations', label: 'Office Locations', icon: '🏢' }
                      ],
                      product: [
                        { key: 'users', label: 'Active Users', icon: '👥' },
                        { key: 'downloads', label: 'Downloads', icon: '📦' },
                        { key: 'rating', label: 'User Rating', icon: '⭐' },
                        { key: 'reviews', label: 'Reviews', icon: '💬' },
                        { key: 'features', label: 'Features', icon: '⚙️' },
                        { key: 'integrations', label: 'Integrations', icon: '🔗' }
                      ],
                      service: [
                        { key: 'clients', label: 'Clients Served', icon: '👥' },
                        { key: 'projects', label: 'Projects Completed', icon: '📦' },
                        { key: 'satisfaction', label: 'Client Satisfaction', icon: '⭐' },
                        { key: 'testimonials', label: 'Testimonials', icon: '💬' },
                        { key: 'certifications', label: 'Certifications', icon: '🏅' },
                        { key: 'experience', label: 'Years Experience', icon: '📅' }
                      ],
                      project: [
                        { key: 'team', label: 'Team Size', icon: '👥' },
                        { key: 'duration', label: 'Project Duration', icon: '📅' },
                        { key: 'budget', label: 'Budget Range', icon: '📦' },
                        { key: 'technologies', label: 'Technologies Used', icon: '⚙️' },
                        { key: 'milestones', label: 'Milestones', icon: '🏆' },
                        { key: 'stakeholders', label: 'Stakeholders', icon: '🏢' }
                      ]
                    };

                    const availableStats = PROFILE_TYPE_STATS[profileType] || PROFILE_TYPE_STATS.personal;
                    const statConfig = availableStats.find(s => s.key === statKey);
                    const statValue = heroSection.statsValues?.[statKey];

                    // Skip if no config or value found
                    if (!statConfig || !statValue) return null;

                    return (
                      <div 
                        key={idx}
                        className="rounded-xl p-6 border hover:scale-105 transition-all duration-300"
                        style={{
                          background: `${customStyles.primaryColor}22`,
                          borderColor: `${customStyles.primaryColor}44`,
                          backdropFilter: 'blur(8px)'
                        }}
                      >
                        <div className="text-2xl mb-2">{statConfig.icon}</div>
                        <div className="text-3xl font-bold mb-2 text-white">{statValue}</div>
                        <div className="text-white/70 text-sm">{statConfig.label}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Fallback for legacy data - Remove this section once all profiles are migrated */}
              {!heroSection.selectedStats && (
                <div className="grid grid-cols-2 gap-6">
                  {/* Legacy Product/Service Stats */}
                  {(profileType === 'product' || profileType === 'service') && heroSection.quickStats && (
                    Object.entries(heroSection.quickStats).map(([key, value], idx) => (
                      <div 
                        key={idx}
                        className="rounded-xl p-6 border hover:scale-105 transition-all duration-300"
                        style={{
                          background: `${customStyles.primaryColor}22`,
                          borderColor: `${customStyles.primaryColor}44`,
                          backdropFilter: 'blur(8px)'
                        }}
                      >
                        <div className="text-3xl font-bold mb-2 text-white">{value}</div>
                        <div className="text-white/70 text-sm capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                      </div>
                    ))
                  )}

                  {/* Legacy Organization Stats */}
                  {profileType === 'organization' && (
                    <>
                      {heroSection.teamSize && (
                        <div 
                          className="rounded-xl p-6 border hover:scale-105 transition-all duration-300"
                          style={{
                            background: `${customStyles.accentColor}22`,
                            borderColor: `${customStyles.accentColor}44`,
                            backdropFilter: 'blur(8px)'
                          }}
                        >
                          <div className="text-2xl mb-2">👥</div>
                          <div className="text-3xl font-bold mb-2 text-white">{heroSection.teamSize}</div>
                          <div className="text-white/70 text-sm">Team Members</div>
                        </div>
                      )}
                      
                      {heroSection.yearsInBusiness && (
                        <div 
                          className="rounded-xl p-6 border hover:scale-105 transition-all duration-300"
                          style={{
                            background: `${customStyles.accentColor}22`,
                            borderColor: `${customStyles.accentColor}44`,
                            backdropFilter: 'blur(8px)'
                          }}
                        >
                          <div className="text-2xl mb-2">⏰</div>
                          <div className="text-3xl font-bold mb-2 text-white">{heroSection.yearsInBusiness}</div>
                          <div className="text-white/70 text-sm">Years in Business</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Value Proposition / Availability status with custom colors */}
              <div 
                className="rounded-xl p-6 border"
                style={{
                  background: `${customStyles.primaryColor}66`,
                  borderColor: `${customStyles.primaryColor}66`
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  {(heroSection.showStatusIndicator !== false) && (
                    <div 
                      className="w-3 h-3 rounded-full animate-pulse"
                      style={{ background: customStyles.primaryColor }}
                    />
                  )}
                  <span className="font-semibold text-white">
                    {heroSection.valueProposition ? 'Value Proposition' : 
                    (profileType === 'personal' ? 'Available for Work' : 
                      profileType === 'service' ? 'Accepting New Clients' :
                      profileType === 'product' ? 'Available Now' :
                      profileType === 'organization' ? 'Open for Partnerships' : 'Available')}
                  </span>
                </div>
                <p className="text-white/90 text-md leading-relaxed">
                  {heroSection.valueProposition || 
                  (heroSection.showDefaultStatus !== false ? 
                    (profileType === 'personal' ? 'Open to new opportunities and collaborations' :
                    profileType === 'service' ? 'Ready to help with your next project' :
                    profileType === 'product' ? 'Ready to ship and integrate' :
                    profileType === 'organization' ? 'Building partnerships that drive innovation' :
                    'Ready to collaborate and create value') : '')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator with custom color */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <ChevronDown 
            className="w-6 h-6" 
            style={{ color: customStyles.accentColor }}
          />
        </div>
      </div>

      {/* Dynamic Navigation Tabs */}
      <div className="bg-white border-b sticky top-0 z-40 shadow-sm backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto scrollbar-hide">
            {navigationTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap font-medium transition-all duration-200 hover:scale-105 relative ${
                    isActive ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-600 hover:text-purple-500'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="profile-hero-enhanced">
      {renderEnhancedHero()}
    
    {/* Add this right after your hero section closes */}
      <TestimonialSubmissionModal
        isOpen={showTestimonialModal}
        onClose={() => setShowTestimonialModal(false)}
        profileId={bookId}
        profileOwnerName={book?.botName || 'Profile Owner'}
      />

      <TestimonialViewModal
        isOpen={showTestimonialViewModal}
        onClose={() => setShowTestimonialViewModal(false)}
        profileId={bookId}
        profileOwnerName={book?.botName || 'Profile Owner'}
        publicTestimonials={profileSections.find(s => s.type === 'testimonials')?.items?.filter(item => item.showOnWebsite === true) || []}
      />
    </div>
  );
};

// Enhanced CSS for the hero section
const heroStyles = `
  .profile-hero-enhanced {
    position: relative;
  }

  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  /* Glassmorphism effects */
  .backdrop-blur-sm {
    backdrop-filter: blur(8px);
  }

  /* Smooth scroll behavior */
  .profile-hero-enhanced {
    scroll-behavior: smooth;
  }

  /* Enhanced button hover effects */
  .profile-hero-enhanced button {
    position: relative;
    overflow: hidden;
  }

  .profile-hero-enhanced button::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  .profile-hero-enhanced button:hover::before {
    left: 100%;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .profile-hero-enhanced .grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    
    .profile-hero-enhanced .text-6xl {
      font-size: 2.5rem;
    }
    
    .profile-hero-enhanced .text-2xl {
      font-size: 1.25rem;
    }
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('profile-hero-enhanced-styles') || document.createElement('style');
  styleElement.id = 'profile-hero-enhanced-styles';
  styleElement.textContent = heroStyles;
  if (!document.getElementById('profile-hero-enhanced-styles')) {
    document.head.appendChild(styleElement);
  }
}

// Helper Components (make sure these are defined if they don't exist)
const ProfileImage = ({ heroSection, ProfileIcon, size, centered, white }) => {
  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-20 h-20', 
    large: 'w-24 h-24 md:w-32 md:h-32'
  };

  const containerClass = centered ? 'mx-auto' : '';
  const borderClass = white ? 'border-white' : 'border-white';

  return (
    <div className={`relative ${containerClass}`}>
      <div className={`${sizeClasses[size]} rounded-full border-4 ${borderClass} shadow-lg overflow-hidden bg-gray-200`}>
        {heroSection.profileImageUrl ? (
          <img 
            src={heroSection.profileImageUrl}
            alt={heroSection.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ProfileIcon className={`${size === 'large' ? 'h-16 w-16' : size === 'medium' ? 'h-12 w-12' : 'h-8 w-8'} text-gray-400`} />
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileInfo = ({ heroSection, book, centered, minimal }) => {
  const titleClass = minimal ? 'text-lg font-semibold mb-1' : 'text-2xl font-bold mb-2';
  const containerClass = centered ? 'text-center' : '';

  return (
    <div className={containerClass}>
      <h1 className={titleClass}>
        {heroSection.name || book.botName}
      </h1>
      {heroSection.title && (
        <p className={`text-blue-600 font-medium ${minimal ? 'text-base mb-2' : 'text-xl mb-3'}`}>
          {heroSection.title}
        </p>
      )}
      {heroSection.tagline && (
        <p className={`text-gray-700 ${minimal ? 'text-sm' : 'text-lg'}`}>
          {heroSection.tagline}
        </p>
      )}
    </div>
  );
};

const ActionButtons = ({ interactions, onInteraction, centered, minimal, vertical }) => {
  const containerClass = vertical ? 'flex flex-col gap-3' : 'flex items-center gap-3';
  const justifyClass = centered ? 'justify-center' : '';

  return (
    <div className={`${containerClass} ${justifyClass}`}>
      <button
        onClick={() => onInteraction('like')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
          interactions.liked
            ? 'bg-red-50 border-red-200 text-red-600'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Heart className={`h-4 w-4 ${interactions.liked ? 'fill-current' : ''}`} />
        <span>{interactions.likeCount || 0}</span>
      </button>

      <button
        onClick={() => onInteraction('share')}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Share2 className="h-4 w-4" />
        <span>Share</span>
      </button>
    </div>
  );
};

const ProfileStats = ({ interactions }) => (
  <div className="flex items-center gap-6 text-sm text-gray-500 mt-4">
    <div className="flex items-center gap-1">
      <Eye className="h-4 w-4" />
      <span>{interactions.viewCount || 0} views</span>
    </div>
  </div>
);

// Enhanced Main Content Component
const ProfileMainContent = ({ 
  profileSections, 
  profileType, 
  sectionLayout, 
  sectionVisibility, 
  sectionOrder, 
  showSectionIcons,
  showSectionNumbers,
  enableSectionCollapse,
  onContactReference,
  layout,
  contentRef // NEW: Receive contentRef
}) => {
  const getSectionComponent = (section, index) => {
    const sectionProps = {
      section,
      index,
      sectionLayout,
      showSectionIcons,
      showSectionNumbers,
      enableSectionCollapse,
      layout
    };

    switch (section.type) {
      case 'about':
        return <AboutSection key={section.id} {...sectionProps} profileType={profileType} />;
      case 'experience':
        return <ExperienceSection key={section.id} {...sectionProps} />;
      case 'education':
        return <EducationSection key={section.id} {...sectionProps} />;
      case 'skills':
        return <SkillsSection key={section.id} {...sectionProps} />;
      case 'projects':
        return <ProjectsSection key={section.id} {...sectionProps} />;
      case 'services':
        return <ServicesSection key={section.id} {...sectionProps} />;
      case 'achievements':
        return <AchievementsSection key={section.id} {...sectionProps} />;
      case 'features':
        return <FeaturesSection key={section.id} {...sectionProps} />;
      case 'gallery':
        return <GallerySection key={section.id} {...sectionProps} />;
      case 'testimonials':
        return <TestimonialsSection key={section.id} {...sectionProps} />;
      case 'stats':
        return <StatsSection key={section.id} {...sectionProps} />;
      case 'references':
        return (
          <ReferencesSection 
            key={`${section.type}-${index}`} 
            {...sectionProps}
            onContactReference={onContactReference}
          />
        );
      case 'story':
        return <StorySection key={section.id} {...sectionProps} />;
      case 'demo':
        return <DemoSection key={section.id} {...sectionProps} />;
      case 'specifications':
        return <SpecificationsSection key={section.id} {...sectionProps} />;
      case 'credentials':
        return <CredentialsSection key={section.id} {...sectionProps} />;
      case 'pricing':
        return <PricingSection key={section.id} {...sectionProps} />;
      case 'engagement':
        return <EngagementSection key={section.id} {...sectionProps} />;
      default:
        return null;
    }
  };

  // Filter and sort sections based on visibility and order
  const visibleSections = profileSections
    .filter(section => sectionVisibility[section.type] !== false && !['hero'].includes(section.type)) // Remove hero from main content
    .sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.type);
      const bIndex = sectionOrder.indexOf(b.type);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });

  const getMainContentClasses = () => {
    if (layout === 'minimal') return 'space-y-12';
    if (layout === 'creative') return 'space-y-8 masonry-layout';
    return layout === 'classic' ? 'lg:col-span-2 space-y-10' : 'lg:col-span-3 space-y-8';
  };

  return (
    <div className={getMainContentClasses()}>
      {visibleSections.map((section, index) => (
        // Enhanced section wrapper with better scroll targeting
        <div 
          key={section.id} 
          id={`section-${section.type}`} 
          className="section-scroll-target scroll-mt-32"
        >
          <div className="section-wrapper">
            {getSectionComponent(section, index)}
          </div>
        </div>
      ))}
      
      {/* Contact section - always show at the end if it exists */}
      {profileSections.find(s => s.type === 'contact') && sectionVisibility.contact !== false && (
        <div 
          id="section-contact" 
          className="section-scroll-target scroll-mt-32"
        >
          <div className="section-wrapper">
            {getSectionComponent(profileSections.find(s => s.type === 'contact'), visibleSections.length)}
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Section Components with Layout Support
const SectionWrapper = ({ children, title, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, icon, layout }) => {
  const [collapsed, setCollapsed] = useState(false);

  const getSectionClasses = () => {
    const base = 'section-wrapper';
    const layoutClasses = {
      card: 'bg-white rounded-md border border-gray-200 p-0 lg:p-6',
      list: 'bg-white border-l-4 border-purple-500 p-2 lg:p-6 pl-8 mb-6',
      timeline: 'relative pl-8 pb-8 border-l-2 border-gray-200 ml-4',
      grid: 'bg-white rounded-lg border border-gray-200 p-4'
    };
    
    const shadowClass = 'shadow-[var(--profile-shadow)]';
    const radiusClass = 'rounded-[var(--profile-border-radius)]';
    
    return `${base} ${layoutClasses[sectionLayout]} ${shadowClass} ${radiusClass}`;
  };

  const renderSectionHeader = () => (
    <div className={`section-header flex items-center justify-between mb-6 ${enableSectionCollapse ? 'cursor-pointer' : ''}`} 
         onClick={enableSectionCollapse ? () => setCollapsed(!collapsed) : undefined}>
      <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
        {showSectionNumbers && (
          <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-medium">
            {index + 1}
          </span>
        )}
        {showSectionIcons && icon}
        {title}
      </h2>
      {enableSectionCollapse && (
        <button className="p-1 hover:bg-gray-100 rounded">
          <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );

  if (sectionLayout === 'timeline') {
    return (
      <div className={getSectionClasses()}>
        <div className="absolute w-3 h-3 bg-purple-500 rounded-full -left-2 top-2"></div>
        {renderSectionHeader()}
        {(!enableSectionCollapse || !collapsed) && children}
      </div>
    );
  }

  return (
    <div className={getSectionClasses()}>
      {renderSectionHeader()}
      {(!enableSectionCollapse || !collapsed) && children}
    </div>
  );
};

const AboutSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, profileType, profileSections, layout }) => (
   <SectionWrapper
    title={profileType === 'organization' ? 'About Us' : 
           profileType === 'project' ? 'Project Overview' :
           profileType === 'asset' ? 'Product Description' : 
           profileType === 'product' ? 'About This Product' :
           profileType === 'service' ? 'About Our Service' : 'About'}
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<User className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    {/* ENHANCED: Modern grid layout with visual elements */}
    <div className="space-y-8">
      
      {/* Main Description with Enhanced Typography */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 lg:rounded-2xl p-4 border border-blue-100">
        <div className="prose prose-lg max-w-none">
          <MathRenderer
            content={section.description || ''}
            className="text-gray-800 leading-relaxed text-lg"
            enableMarkdown={true}
            enableMath={true}
          />
        </div>
      </div>

      {/* Mission & Vision with Modern Cards */}
      {(section.mission || section.vision) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {section.mission && (
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="absolute top-6 right-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                Mission
              </h3>
              <div className="prose prose-gray max-w-none">
                <MathRenderer
                  content={section.mission}
                  className="text-gray-700 leading-relaxed"
                  enableMarkdown={true}
                />
              </div>
            </div>
          )}
          
          {section.vision && (
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="absolute top-6 right-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                Vision
              </h3>
              <div className="prose prose-gray max-w-none">
                <MathRenderer
                  content={section.vision}
                  className="text-gray-700 leading-relaxed"
                  enableMarkdown={true}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {profileType === 'product' && section.features && section.features.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Product Highlights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Key Features */}
            {section.features && section.features.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Key Features
                </h4>
                <div className="space-y-3">
                  {section.features.filter(f => f.trim()).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Product Stats */}
            {section.stats && section.stats.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Product Stats
                </h4>
                <div className="space-y-4">
                  {section.stats.filter(s => s.label && s.value).map((stat, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-gray-600">{stat.label}</span>
                      <span className="font-semibold text-blue-600">{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {profileType === 'service' && (
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Why Choose Our Services</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: 'Fast Delivery', desc: '24-48 hour turnaround' },
              { icon: Users, title: 'Expert Team', desc: 'Certified professionals' },
              { icon: Shield, title: 'Quality Assured', desc: '100% satisfaction guarantee' },
              { icon: DollarSign, title: 'Competitive Pricing', desc: 'Best value for money' }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {profileType === 'organization' && (
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Company Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">2019</div>
              <div className="text-sm text-gray-600">Founded</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">50+</div>
              <div className="text-sm text-gray-600">Team Members</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">500+</div>
              <div className="text-sm text-gray-600">Projects Delivered</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">25+</div>
              <div className="text-sm text-gray-600">Countries Served</div>
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-4 text-white text-center">
        <h3 className="text-xl font-bold mb-4">Ready to Get Started?</h3>
        <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
          {profileType === 'personal' ? "Let's work together to bring your ideas to life." :
           profileType === 'product' ? "Experience the difference our product can make." :
           profileType === 'service' ? "Let us help you achieve your goals." :
           "Partner with us for exceptional results."}
        </p>
        <button className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 mx-auto">
          <Mail className="w-5 h-5" />
          Get In Touch
        </button>
      </div>
    </div>
  </SectionWrapper>
);

const EducationSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Education"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Calendar className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className="space-y-6">
      {section.items?.map((edu, idx) => (
        <div key={idx} className={`education-item ${sectionLayout === 'timeline' ? 'pl-4' : 'flex gap-4'}`}>
          {sectionLayout !== 'timeline' && (
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
            </div>
          )}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center mb-2">
              <h3 className="font-semibold text-gray-900">{edu.degree}</h3>
              <span className="text-sm text-gray-500">
                {edu.startDate && edu.endDate ? `${edu.startDate} - ${edu.endDate}` : 
                 edu.year || edu.graduationYear || ''}
              </span>
            </div>
            <p className="text-purple-600 font-medium mb-1">{edu.institution}</p>
            {edu.field && (
              <p className="text-gray-600 text-sm mb-2">{edu.field}</p>
            )}
            {edu.gpa && (
              <p className="text-gray-600 text-sm mb-2">GPA: {edu.gpa}</p>
            )}
            {edu.description && (
              <MathRenderer
                  content={edu.description}
                  className="text-gray-700 text-sm"
                  enableMarkdown={true}
                />
            )}
            {edu.achievements?.length > 0 && (
              <ul className="text-sm text-gray-600">
                {edu.achievements.map((achievement, achievementIdx) => (
                  <li key={achievementIdx} className="flex items-start gap-2">
                    <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                    {achievement}
                  </li>
                ))}
              </ul>
            )}
            {edu.honors?.length > 0 && (
              <div className="mt-2">
                <div className="flex flex-wrap gap-1">
                  {edu.honors.map((honor, honorIdx) => (
                    <span key={honorIdx} className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                      {honor}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

const ExperienceSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Work Experience"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Briefcase className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className="space-y-6">
      {section.items?.map((item, idx) => (
        <div key={idx} className={`experience-item ${sectionLayout === 'timeline' ? 'pl-4' : 'flex gap-4'}`}>
          {sectionLayout !== 'timeline' && (
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
            </div>
          )}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
              <h3 className="font-semibold text-gray-900">{item.position}</h3>
              <span className="text-sm text-gray-500">{item.duration || `${item.startDate} - ${item.endDate || 'Present'}`}</span>
            </div>
            <p className="text-purple-600 font-medium mb-2">{item.company}</p>
            {item.description && 
            <MathRenderer
              content={item.description}
              className="text-gray-700 text-sm"
              enableMarkdown={true}
            />}
            {item.achievements?.length > 0 && (
              <ul className="mt-2 text-sm text-gray-600">
                {item.achievements.map((achievement, achievementIdx) => (
                  <li key={achievementIdx} className="flex items-start gap-2">
                    <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                    {achievement}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

const SkillsSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Skills & Expertise"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Star className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className="space-y-6">
      {section.categories?.map((category, categoryIdx) => (
        <div key={categoryIdx}>
          <h3 className="font-medium text-gray-900 mb-3">{category.name}</h3>
          <div className="flex flex-wrap gap-2">
            {category.skills?.map((skill, skillIdx) => (
              <div key={skillIdx} className="skill-tag">
                <span 
                  className="px-3 py-1 text-sm rounded-full border"
                  style={{ 
                    backgroundColor: 'var(--profile-primary-color)10',
                    borderColor: 'var(--profile-primary-color)30',
                    color: 'var(--profile-primary-color)'
                  }}
                >
                  {typeof skill === 'string' ? skill : skill.name}
                </span>
                {typeof skill === 'object' && skill.level && (
                  <div className="flex items-center ml-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`h-3 w-3 ${star <= skill.level ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )) || (
        <div className="flex flex-wrap gap-2">
          {section.items?.map((skill, idx) => (
            <span 
              key={idx}
              className="px-3 py-1 text-sm rounded-full border"
              style={{ 
                backgroundColor: 'var(--profile-primary-color)10',
                borderColor: 'var(--profile-primary-color)30',
                color: 'var(--profile-primary-color)'
              }}
            >
              {typeof skill === 'string' ? skill : skill.name}
            </span>
          ))}
        </div>
      )}
    </div>
  </SectionWrapper>
);

const ProjectsSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Projects"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Briefcase className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className={sectionLayout === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-6"}>
      {section.items?.map((project, idx) => (
        <div key={idx} className="project-card border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
          {project.image && (
            <img 
              src={project.image}
              alt={project.title}
              className="w-full h-32 object-cover rounded-lg mb-3"
            />
          )}
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-gray-900">{project.title}</h3>
            <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ml-2 ${
              project.status === 'completed' ? 'bg-green-100 text-green-700' :
              project.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {project.status}
            </span>
          </div>
          <MathRenderer
              content={project.description}
              className="text-gray-700 text-sm"
              enableMarkdown={true}
          />
          
          {project.technologies?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {project.technologies.map((tech, techIdx) => (
                <span key={techIdx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                  {tech}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 text-sm">
            {project.projectUrl && (
              <a
                href={project.projectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-700 flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                Demo
              </a>
            )}
            {project.githubUrl && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-700 flex items-center gap-1"
              >
                <Github className="h-3 w-3" />
                Code
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

const AchievementsSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Achievements & Awards"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Trophy className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className="space-y-4">
      {section.items?.map((achievement, idx) => (
        <div key={idx} className="achievement-card flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Trophy className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{achievement.title}</h3>
                <MathRenderer
                  content={achievement.description}
                  className="text-gray-700 text-sm"
                  enableMarkdown={true}
                />
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>{achievement.organization}</span>
                  {achievement.date && (
                    <>
                      <span>•</span>
                      <span>{new Date(achievement.date).toLocaleDateString()}</span>
                    </>
                  )}
                  {achievement.isVerified && (
                    <>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span className="text-green-600 font-medium">Verified</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {achievement.credentialUrl && (
                <a
                  href={achievement.credentialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-700 flex-shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// Add more section components...
const ServicesSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Services & Products"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Package className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className={sectionLayout === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-6" : "space-y-6"}>
      {section.items?.map((service, idx) => (
        <div key={idx} className="service-card border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
          {service.image && (
            <img 
              src={service.image}
              alt={service.title}
              className="w-full h-32 object-cover rounded-lg mb-3"
            />
          )}
          <h3 className="font-semibold text-gray-900 mb-2">{service.title}</h3>
          <MathRenderer
              content={service.description}
              className="text-gray-700 text-sm"
              enableMarkdown={true}
          />
          
          {service.price && (
            <p className="text-lg font-bold mb-2" style={{ color: 'var(--profile-primary-color)' }}>
              {service.price}
            </p>
          )}
          
          {service.features?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {service.features.slice(0, 3).map((feature, featureIdx) => (
                <span key={featureIdx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {feature}
                </span>
              ))}
              {service.features.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{service.features.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  </SectionWrapper>
);

const FeaturesSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Key Features"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<CheckCircle className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className="space-y-4">
      {section.items?.map((feature, idx) => (
        <div key={idx} className="feature-item flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                <MathRenderer
                  content={feature.description}
                  className="text-gray-700 text-sm"
                  enableMarkdown={true}
                />
              </div>
              {feature.status && (
                <span className={`px-2 py-1 text-xs rounded-full ml-4 flex-shrink-0 ${
                  feature.status === 'completed' ? 'bg-green-100 text-green-700' :
                  feature.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                  feature.status === 'planned' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {feature.status}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

const GallerySection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => {
  const [viewMode, setViewMode] = useState('masonry');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleMediaClick = (mediaItem, itemIndex) => {
    setSelectedMedia(mediaItem);
    setLightboxIndex(itemIndex);
  };

  // Get display settings with defaults
  const showTitles = section.showTitles !== false; // default true
  const showDescriptions = section.showDescriptions !== false; // default true
  const columns = section.columns || 3;

  const MediaCard = ({ mediaItem, itemIndex }) => (
    <div 
      className="group relative overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-xl cursor-pointer transition-all duration-300 border border-gray-100"
      onClick={() => handleMediaClick(mediaItem, itemIndex)}
    >
      <div className="relative aspect-video">
        {mediaItem.type === 'image' || !mediaItem.type ? (
          <img 
            src={mediaItem.url}
            alt={mediaItem.title || `Gallery item ${itemIndex + 1}`}
            className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : mediaItem.type === 'video' ? (
          <div className="relative w-full h-full">
            <video 
              src={mediaItem.url}
              className="w-full h-auto object-cover"
              poster={mediaItem.thumbnail}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-all duration-300">
              <div className="bg-white/90 rounded-full p-4">
                <Play className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
            <Package className="h-16 w-16 text-purple-300" />
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-4">
          <div className="flex items-center gap-2 text-white">
            <ZoomIn className="h-5 w-5" />
            <span className="text-sm font-medium">View</span>
          </div>
        </div>
      </div>

      {/* Title and Description */}
      {(showTitles || showDescriptions) && (mediaItem.title || mediaItem.description) && (
        <div className="p-4 space-y-2">
          {showTitles && mediaItem.title && (
            <h4 className="font-semibold text-gray-900 text-md line-clamp-2">
              {mediaItem.title}
            </h4>
          )}
          {showDescriptions && mediaItem.description && (
            <MathRenderer
              content={mediaItem.description}
              className="text-xs text-gray-600 line-clamp-3"
              enableMarkdown={true}
            />
          )}
          {/* Tags if they exist */}
          {mediaItem.tags && mediaItem.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {mediaItem.tags.slice(0, 3).map((tag, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 text-xs bg-purple-50 text-purple-600 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const MediaLightbox = () => {
    if (!selectedMedia) return null;

    return (
      <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="relative w-full max-w-6xl max-h-full flex flex-col">
          {/* Close button */}
          <button
            onClick={() => setSelectedMedia(null)}
            className="absolute top-4 right-4 z-10 bg-white/10 backdrop-blur-md text-white p-3 rounded-full hover:bg-white/20 transition-all"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Media content */}
          <div className="flex-1 flex items-center justify-center mb-4">
            {selectedMedia.type === 'video' ? (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="max-w-full max-h-[70vh] rounded-lg"
              />
            ) : (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.title || 'Gallery item'}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            )}
          </div>

          {/* Navigation arrows */}
          {section.items?.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = lightboxIndex > 0 ? lightboxIndex - 1 : section.items.length - 1;
                  setLightboxIndex(newIndex);
                  setSelectedMedia(section.items[newIndex]);
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/10 backdrop-blur-md text-white p-4 rounded-full hover:bg-white/20 transition-all"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = lightboxIndex < section.items.length - 1 ? lightboxIndex + 1 : 0;
                  setLightboxIndex(newIndex);
                  setSelectedMedia(section.items[newIndex]);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/10 backdrop-blur-md text-white p-4 rounded-full hover:bg-white/20 transition-all"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Caption area */}
          {(selectedMedia.title || selectedMedia.description) && (
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 text-white max-w-4xl mx-auto">
              {selectedMedia.title && (
                <h3 className="font-semibold text-xl mb-2">{selectedMedia.title}</h3>
              )}
              {selectedMedia.description && (
                <MathRenderer
                  content={selectedMedia.description}
                  className="text-sm text-gray-200 leading-relaxed"
                  enableMarkdown={true}
                />
              )}
              {selectedMedia.tags && selectedMedia.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedMedia.tags.map((tag, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 text-xs bg-white/20 backdrop-blur-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="text-xs text-gray-300 mt-4">
                {lightboxIndex + 1} / {section.items.length}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <SectionWrapper
      title={section.title || "Gallery"}
      index={index}
      sectionLayout={sectionLayout}
      showSectionIcons={showSectionIcons}
      showSectionNumbers={showSectionNumbers}
      enableSectionCollapse={enableSectionCollapse}
      icon={<ImageIcon className="h-5 w-5 text-purple-600" />}
      layout={layout}
    >
      {/* Gallery Controls */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-full">
            <ImageIcon className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">
              {section.items?.length || 0} {section.items?.length === 1 ? 'item' : 'items'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('masonry')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'masonry' 
                ? 'bg-purple-600 text-white shadow-md' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Grid view"
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'list' 
                ? 'bg-purple-600 text-white shadow-md' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="List view"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Gallery Content */}
      {section.items && section.items.length > 0 ? (
        viewMode === 'masonry' ? (
          <ResponsiveMasonry
            columnsCountBreakPoints={{
              350: 1,
              640: 2,
              1024: columns,
              1280: Math.min(columns, 4)
            }}
          >
            <Masonry gutter="1rem">
              {section.items.map((mediaItem, itemIndex) => (
                <MediaCard
                  key={mediaItem.id || itemIndex}
                  mediaItem={mediaItem}
                  itemIndex={itemIndex}
                />
              ))}
            </Masonry>
          </ResponsiveMasonry>
        ) : (
          <div className="space-y-3">
            {section.items.map((mediaItem, itemIndex) => (
              <div
                key={mediaItem.id || itemIndex}
                className="flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-lg hover:shadow-md cursor-pointer transition-all group"
                onClick={() => handleMediaClick(mediaItem, itemIndex)}
              >
                {/* Thumbnail */}
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  {mediaItem.type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
                      <Play className="h-8 w-8 text-purple-600" />
                    </div>
                  ) : (
                    <img
                      src={mediaItem.url}
                      alt={mediaItem.title || ''}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {showTitles && mediaItem.title && (
                    <h4 className="font-semibold text-gray-900 mb-1 truncate group-hover:text-purple-600 transition-colors">
                      {mediaItem.title}
                    </h4>
                  )}
                  {showDescriptions && mediaItem.description && (
                    <MathRenderer
                      content={mediaItem.description}
                      className="text-sm text-gray-600 line-clamp-2"
                      enableMarkdown={true}
                    />
                  )}
                  {mediaItem.tags && mediaItem.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {mediaItem.tags.slice(0, 4).map((tag, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-0.5 text-xs bg-purple-50 text-purple-600 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* View icon */}
                <ZoomIn className="h-5 w-5 text-gray-400 group-hover:text-purple-600 transition-colors flex-shrink-0" />
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
          <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-base font-medium text-gray-900 mb-2">No gallery items yet</h3>
          <p className="text-sm text-gray-500">Gallery items will appear here when added</p>
        </div>
      )}

      <MediaLightbox />
    </SectionWrapper>
  );
};

const TestimonialsSection = ({ 
  section, 
  index, 
  sectionLayout, 
  showSectionIcons, 
  showSectionNumbers, 
  enableSectionCollapse, 
  layout
}) => {
  // Only show public testimonials
  const publicTestimonials = section.items?.filter(item => item.showOnWebsite === true) || [];

  return (
    <SectionWrapper
      id="testimonials-section" // Add ID for scrolling
      title="Reviews and Reflections"
      index={index}
      sectionLayout={sectionLayout}
      showSectionIcons={showSectionIcons}
      showSectionNumbers={showSectionNumbers}
      enableSectionCollapse={enableSectionCollapse}
      icon={<MessageCircle className="h-5 w-5 text-purple-600" />}
      layout={layout}
    >
      {publicTestimonials.length > 0 ? (
        <div className={sectionLayout === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-6" : "space-y-6"}>
          {publicTestimonials.map((testimonial, idx) => (
            <div key={idx} className="testimonial-card bg-gray-50 border border-gray-200 rounded-lg p-6 relative">
              {/* Quote Icon */}
              <div className="absolute -top-2 -left-2 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-lg font-bold">"</span>
              </div>
              
              {/* Testimonial Content */}
              <div className="mb-4">
                <p className="text-gray-700 text-sm leading-relaxed">
                  {testimonial.testimonial}
                </p>
              </div>
              
              {/* Author Info */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                  {testimonial.clientPhoto ? (
                    <img 
                      src={testimonial.clientPhoto} 
                      alt={testimonial.clientName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{testimonial.clientName}</h4>
                  {(testimonial.clientTitle || testimonial.clientCompany) && (
                    <p className="text-sm text-gray-600">
                      {testimonial.clientTitle}
                      {testimonial.clientTitle && testimonial.clientCompany && ' at '}
                      {testimonial.clientCompany}
                    </p>
                  )}
                  {testimonial.date && (
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(testimonial.date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Rating */}
              <div className="flex items-center gap-1 mt-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`h-4 w-4 ${
                      star <= (testimonial.rating || 5)
                        ? 'text-yellow-400 fill-current' 
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              
              {/* Project/Service Reference */}
              {testimonial.projectTitle && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Project:</span> {testimonial.projectTitle}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No public testimonials to display</p>
        </div>
      )}
    </SectionWrapper>
  );
};

const ReferencesSection = ({ 
  section, 
  index, 
  sectionLayout, 
  showSectionIcons, 
  showSectionNumbers, 
  enableSectionCollapse, 
  layout,
  onContactReference // NEW: Handler passed as prop
}) => (
  <SectionWrapper
    title="References"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Users className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    {section.items && section.items.length > 0 ? (
      <div className={sectionLayout === 'grid' ? 
        'grid grid-cols-1 md:grid-cols-2 gap-6' : 
        'space-y-6'
      }>
        {section.items.map((reference, refIndex) => (
          <div key={reference.id || refIndex} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            {/* Reference Header */}
            <div className="mb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{reference.name}</h4>
                  {reference.title && (
                    <p className="text-sm text-gray-600">{reference.title}</p>
                  )}
                  {reference.company && (
                    <p className="text-sm text-gray-500">{reference.company}</p>
                  )}
                </div>
                {reference.canContact !== false && (
                  <div className="flex items-center text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Available
                  </div>
                )}
              </div>
              
              {reference.relationship && (
                <div className="mt-2">
                  <span className="inline-flex items-center text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                    {reference.relationship.replace('-', ' ')}
                  </span>
                </div>
              )}
            </div>

            {/* Reference Description */}
            {reference.description && (
              <div className="mb-3">
                <p className="text-sm text-gray-600">{reference.description}</p>
              </div>
            )}

            {/* Contact Information */}
            {section.showContactInfo !== false && reference.canContact !== false && (
              <div className="mb-3">
                <div className="flex flex-wrap gap-4 text-sm">
                  {reference.email && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Mail className="h-3 w-3" />
                      <span>{reference.email}</span>
                    </div>
                  )}
                  {reference.phone && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Phone className="h-3 w-3" />
                      <span>{reference.phone}</span>
                    </div>
                  )}
                  {reference.isLinkedIn && reference.linkedInUrl && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <LinkIcon className="h-3 w-3" />
                      <a 
                        href={reference.linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-purple-600 transition-colors"
                      >
                        LinkedIn
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reference Document */}
            {section.showDocuments && reference.documentUrl && (
              <div className="border-t border-gray-200 pt-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <File className="h-4 w-4" />
                    <span>Reference Letter</span>
                  </div>
                  <a
                    href={reference.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 transition-colors"
                  >
                    View Document
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            )}

            {/* Contact Form Button */}
            {section.enableContactForm && reference.canContact !== false && (
              <div className="border-t border-gray-200 pt-3 mt-3">
                <button
                  onClick={() => onContactReference(reference)} // FIXED: Use the prop
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                >
                  Contact Reference
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-sm font-medium text-gray-900 mb-2">No references added</h3>
        <p className="text-sm text-gray-500">References will appear here when added.</p>
      </div>
    )}
  </SectionWrapper>
);

const StatsSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title="Statistics"
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Eye className="h-5 w-5 text-purple-600" />}
    layout={layout}
  >
    <div className={sectionLayout === 'grid' ? "grid grid-cols-2 md:grid-cols-4 gap-4" : "grid grid-cols-2 md:grid-cols-3 gap-6"}>
      {section.items?.map((stat, idx) => (
        <div key={idx} className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
          {/* Stat Icon */}
          {stat.icon && (
            <div className="text-3xl mb-2">
              {typeof stat.icon === 'string' ? stat.icon : '📊'}
            </div>
          )}
          
          {/* Stat Value */}
          <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
            {stat.value}
          </div>
          
          {/* Stat Label */}
          <div className="text-sm text-gray-600 font-medium">
            {stat.label}
          </div>
          
          {/* Stat Description */}
          {stat.description && (
            <div className="text-xs text-gray-500 mt-1">
              {stat.description}
            </div>
          )}
          
          {/* Stat Trend */}
          {stat.trend && (
            <div className={`text-xs mt-2 flex items-center justify-center gap-1 ${
              stat.trend.direction === 'up' ? 'text-green-600' : 
              stat.trend.direction === 'down' ? 'text-red-600' : 'text-gray-500'
            }`}>
              {stat.trend.direction === 'up' && '↗'}
              {stat.trend.direction === 'down' && '↘'}
              {stat.trend.direction === 'neutral' && '→'}
              <span>{stat.trend.value}</span>
            </div>
          )}
        </div>
      ))}
      
      {/* Common Business Stats */}
      {section.businessStats && (
        <>
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">⏰</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.businessStats.yearsInBusiness || '0'}
            </div>
            <div className="text-sm text-gray-600 font-medium">Years in Business</div>
          </div>
          
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">👥</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.businessStats.clientsServed || '0'}+
            </div>
            <div className="text-sm text-gray-600 font-medium">Clients Served</div>
          </div>
          
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">✅</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.businessStats.projectsCompleted || '0'}+
            </div>
            <div className="text-sm text-gray-600 font-medium">Projects Completed</div>
          </div>
          
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">⭐</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.businessStats.satisfactionRate || '100'}%
            </div>
            <div className="text-sm text-gray-600 font-medium">Satisfaction Rate</div>
          </div>
        </>
      )}
      
      {/* Personal Stats */}
      {section.personalStats && (
        <>
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">💼</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.personalStats.yearsExperience || '0'}+
            </div>
            <div className="text-sm text-gray-600 font-medium">Years Experience</div>
          </div>
          
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">🚀</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.personalStats.projectsDelivered || '0'}+
            </div>
            <div className="text-sm text-gray-600 font-medium">Projects Delivered</div>
          </div>
          
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">🏆</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.personalStats.awardsWon || '0'}
            </div>
            <div className="text-sm text-gray-600 font-medium">Awards Won</div>
          </div>
          
          <div className="stat-card text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-3xl mb-2">📚</div>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--profile-primary-color)' }}>
              {section.personalStats.skillsLearned || '0'}+
            </div>
            <div className="text-sm text-gray-600 font-medium">Skills Mastered</div>
          </div>
        </>
      )}
    </div>
    
    {(!section.items || section.items.length === 0) && !section.businessStats && !section.personalStats && (
      <div className="text-center py-8">
        <Eye className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No statistics to display</p>
      </div>
    )}
  </SectionWrapper>
);

// Enhanced Sidebar Component
const ProfileSidebar = ({ profileSections, profileType, interactions, onContact, profileSettings, layout }) => {
  if (layout === 'minimal' || layout === 'creative') return null;

  const contactSection = profileSections.find(s => s.type === 'contact') || {};
  const sidebarClasses = layout === 'classic' ? 'lg:col-span-1 space-y-6' : 'lg:col-span-1 space-y-6';

  return (
    <div className={sidebarClasses}>
      {/* Contact Information */}
      {profileSettings?.contactVisibility?.email !== false && contactSection.email && (
        <ContactCard 
          contact={contactSection} 
          onContact={onContact}
          profileSettings={profileSettings}
        />
      )}

      {/* Profile Stats */}
      <StatsCard 
        interactions={interactions}
        profileSections={profileSections}
        profileSettings={profileSettings}
      />

      {/* Quick Actions */}
      <QuickActionsCard 
        contact={contactSection}
        onContact={onContact}
        profileSettings={profileSettings}
      />
    </div>
  );
};

// Enhanced Contact Card
const ContactCard = ({ contact, onContact, profileSettings }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6" style={{ boxShadow: 'var(--profile-shadow)' }}>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact</h3>
    <div className="space-y-2">
      {profileSettings?.contactVisibility?.email !== false && contact.email && (
        <button
          onClick={() => onContact('email', contact.email)}
          className="w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
        >
          <Mail className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">{contact.email}</span>
        </button>
      )}
      
      {profileSettings?.contactVisibility?.phone !== false && contact.phone && (
        <button
          onClick={() => onContact('phone', contact.phone)}
          className="w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
        >
          <Phone className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">{contact.phone}</span>
        </button>
      )}

      {profileSettings?.contactVisibility?.website !== false && contact.website && (
        <button
          onClick={() => onContact('website', contact.website)}
          className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
        >
          <Globe className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">Visit Website</span>
        </button>
      )}
    </div>
    
    {/* Social Links */}
    {profileSettings?.contactVisibility?.social !== false && contact.socialLinks && Object.keys(contact.socialLinks).length > 0 && (
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Social Links</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(contact.socialLinks).map(([platform, url]) => {
            const getSocialIcon = (platform) => {
              const icons = { linkedin: Linkedin, github: Github, twitter: Twitter };
              return icons[platform] || Globe;
            };
            const SocialIcon = getSocialIcon(platform);
            return (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <SocialIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700 capitalize">{platform}</span>
              </a>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

// Enhanced Stats Card
const StatsCard = ({ interactions, profileSections, profileSettings }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6" style={{ boxShadow: 'var(--profile-shadow)' }}>
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Stats</h3>
    <div className="space-y-3">
      {profileSettings?.showProfileViews !== false && (
        <div className="flex justify-between">
          <span className="text-gray-600">Profile Views</span>
          <span className="font-medium">{interactions.viewCount || 0}</span>
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-gray-600">Profile Likes</span>
        <span className="font-medium">{interactions.likeCount || 0}</span>
      </div>
      
      {/* Dynamic stats */}
      {profileSections.find(s => s.type === 'projects')?.items && (
        <div className="flex justify-between">
          <span className="text-gray-600">Projects</span>
          <span className="font-medium">{profileSections.find(s => s.type === 'projects').items.length}</span>
        </div>
      )}
      
      {profileSections.find(s => s.type === 'achievements')?.items && (
        <div className="flex justify-between">
          <span className="text-gray-600">Achievements</span>
          <span className="font-medium">{profileSections.find(s => s.type === 'achievements').items.length}</span>
        </div>
      )}
    </div>
  </div>
);

const StorySection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => {
  const [activeStoryTab, setActiveStoryTab] = useState('overview');

  const storyTabs = [
    { key: 'overview', label: 'Overview', icon: FileText },
    { key: 'problem', label: 'Problem Solved', icon: Target },
    { key: 'solution', label: 'Our Solution', icon: Award },
    { key: 'impact', label: 'Impact & Results', icon: Users }
  ];

  return (
    <SectionWrapper
      title={section.title || "Our Story"}
      index={index}
      sectionLayout={sectionLayout}
      showSectionIcons={showSectionIcons}
      showSectionNumbers={showSectionNumbers}
      enableSectionCollapse={enableSectionCollapse}
      icon={<FileText className="h-5 w-5 text-blue-600" />}
      layout={layout}
    >
      <div className="space-y-6">
        {/* Hero Image/Video */}
        {section.heroMedia && (
          <div className="relative rounded-2xl overflow-hidden bg-gray-100">
            {section.heroMedia.type === 'video' ? (
              <div className="relative aspect-video">
                <video
                  src={section.heroMedia.url}
                  poster={section.heroMedia.thumbnail}
                  controls
                  className="w-full h-full object-cover"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <img 
                src={section.heroMedia.url}
                alt={section.heroMedia.alt || "Product story"}
                className="w-full h-64 md:h-80 object-cover"
              />
            )}
          </div>
        )}

        {/* Story Navigation */}
        {section.enableTabs && (
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {storyTabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveStoryTab(tab.key)}
                    className={`${
                      activeStoryTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    } py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                  >
                    <IconComponent className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Story Content */}
        <div className="prose prose-lg max-w-none">
          <MathRenderer
            content={section.enableTabs ? section[activeStoryTab] || section.content : section.content}
            className="text-gray-700 leading-relaxed"
            enableMarkdown={true}
          />
        </div>

        {/* Key Highlights */}
        {section.highlights && section.highlights.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.highlights.map((highlight, idx) => (
              <div key={idx} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-3">
                  <h4 className="font-semibold text-gray-900">{highlight.title}</h4>
                </div>
                <p className="text-gray-600 text-sm">{highlight.description}</p>
                {highlight.metric && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{highlight.metric.value}</div>
                    <div className="text-xs text-gray-500">{highlight.metric.label}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

// 2. Add Demo Section

<DemoSection/>



// 3. Add Specifications Section
// Update the SpecificationsSection - Replace items mapping only
const SpecificationsSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title={section.title || "Specifications"}
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<FileText className="h-5 w-5 text-green-600" />}
    layout={layout}
  >
    <div className="space-y-6">
      {(section.specifications || []).map((category, catIdx) => (
        <div key={catIdx} className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{category.category}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(category.items || []).map((item, itemIdx) => (
              <div key={itemIdx} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                {/* UPDATED: Use MathRenderer instead of plain span */}
                <div className="flex-1">
                  <MathRenderer
                    content={item}
                    className="text-gray-700"
                    enableMarkdown={true}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// 4. Add Credentials Section
const CredentialsSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title={section.title || "Credentials & Certifications"}
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<Award className="h-5 w-5 text-yellow-600" />}
    layout={layout}
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {(section.credentials || []).map((credential, idx) => (
        <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {credential.issuerLogo ? (
                <img 
                  src={credential.issuerLogo}
                  alt={credential.issuer}
                  className="w-12 h-12 object-contain rounded-lg border border-gray-200"
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center">
                  <Award className="h-6 w-6 text-blue-600" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-900">{credential.name}</h3>
                <p className="text-sm text-gray-600">{credential.issuer}</p>
              </div>
            </div>
            
            {credential.isVerified && (
              <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                <CheckCircle className="h-3 w-3" />
                Verified
              </div>
            )}
          </div>

          {credential.description && (
            <p className="text-gray-700 text-sm mb-3">{credential.description}</p>
          )}
          
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {credential.issueDate && (
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>Issued {new Date(credential.issueDate).getFullYear()}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// 5. Add Pricing Section
const PricingSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => (
  <SectionWrapper
    title={section.title || "Pricing"}
    index={index}
    sectionLayout={sectionLayout}
    showSectionIcons={showSectionIcons}
    showSectionNumbers={showSectionNumbers}
    enableSectionCollapse={enableSectionCollapse}
    icon={<DollarSign className="h-5 w-5 text-green-600" />}
    layout={layout}
  >
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {(section.pricingTiers || []).map((tier, idx) => (
        <div
          key={idx}
          className={`relative rounded-2xl border-2 p-8 ${
            tier.highlighted
              ? 'border-blue-500 bg-blue-50 shadow-lg scale-105'
              : 'border-gray-200 bg-white'
          }`}
        >
          {tier.highlighted && (
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                <Star className="h-3 w-3" />
                Most Popular
              </span>
            </div>
          )}

          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
            <div className="flex items-baseline justify-center">
              <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
              {tier.period && tier.period !== 'one-time' && (
                <span className="text-gray-500 ml-1">/{tier.period}</span>
              )}
            </div>
          </div>

          <ul className="space-y-3 mb-8">
            {(tier.features || []).map((feature, featureIdx) => (
              <li key={featureIdx} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>

          <a
            href={tier.buttonUrl}
            className={`block w-full text-center py-3 px-6 rounded-lg font-medium transition-colors ${
              tier.highlighted
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            {tier.buttonText || 'Get Started'}
          </a>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// 6. Add Engagement Section  
const EngagementSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout, bookId }) => {
  const { user } = useAuth();
  const [engagements, setEngagements] = useState({
    likes: 0,
    shares: 0,
    interests: 0
  });

  return (
    <SectionWrapper
      title={section.title || "Engage With Us"}
      index={index}
      sectionLayout={sectionLayout}
      showSectionIcons={showSectionIcons}
      showSectionNumbers={showSectionNumbers}
      enableSectionCollapse={enableSectionCollapse}
      icon={<Heart className="h-5 w-5 text-red-600" />}
      layout={layout}
    >
      <div className="space-y-6">
        {/* Primary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all text-center bg-white">
            <Heart className="h-6 w-6 mx-auto mb-2 text-gray-600" />
            <div className="font-medium text-sm">I'm Interested</div>
            <div className="text-xs text-gray-500 mt-1">{engagements.interests} interested</div>
          </button>

          <button className="p-4 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition-all text-center bg-white">
            <Share2 className="h-6 w-6 mx-auto mb-2 text-gray-600" />
            <div className="font-medium text-sm">Share</div>
            <div className="text-xs text-gray-500 mt-1">{engagements.shares} shares</div>
          </button>

          <button className="p-4 rounded-xl border-2 border-gray-200 hover:border-yellow-300 transition-all text-center bg-white">
            <BookmarkPlus className="h-6 w-6 mx-auto mb-2 text-gray-600" />
            <div className="font-medium text-sm">Bookmark</div>
          </button>
        </div>

        {/* Call-to-Action Buttons */}
        {section.purchaseOptions && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-4">Ready to get started?</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {section.purchaseOptions.map((option, idx) => (
                <a
                  key={idx}
                  href={option.url}
                  target={option.external ? "_blank" : "_self"}
                  className={`block p-4 rounded-xl text-center transition-all ${
                    option.primary
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">{option.label}</div>
                  {option.price && (
                    <div className="text-sm opacity-90 mt-1">{option.price}</div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

// Quick Actions Card
const QuickActionsCard = ({ contact, onContact, profileSettings }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6" style={{ boxShadow: 'var(--profile-shadow)' }}>
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
    <div className="space-y-3">
      {contact.email && (
        <button
          onClick={() => onContact('email', contact.email)}
          className="w-full py-2 px-4 text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium"
          style={{ backgroundColor: 'var(--profile-primary-color)' }}
        >
          Send Message
        </button>
      )}
      
      {contact.phone && (
        <button
          onClick={() => onContact('whatsapp', contact.phone)}
          className="w-full py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
        >
          WhatsApp
        </button>
      )}
      
      {contact.calendly && (
        <button
          onClick={() => window.open(`https://calendly.com/${contact.calendly}`, '_blank')}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          Schedule Meeting
        </button>
      )}
    </div>
  </div>
);

// Loading Component
const ProfileLoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
  </div>
);